import React, { useState } from "react";
import { Paperclip } from "lucide-react";
import { attachmentDownloadUrl } from "../api/boardApi";
import AttachmentPreview from "./AttachmentPreview";

// Renders an Update body, replacing:
//   @<24-hex>         → mention chip with the user's name
//   ![alt](attId)     → inline thumbnail; click opens lightbox preview
//   [filename](attId) → file chip; click opens lightbox (or download fallback)
//
// On top of those tokens, the text *between* them is rendered with light
// Markdown: **bold**, *italic*, `code`, [link](http(s)://...), bullet (- ...)
// and numbered (1. ...) lists, plus newline-to-<br>. The patterns are kept
// deliberately conservative to avoid mangling user text — anything that
// doesn't match a Markdown rule renders as plain text.
//
// Patterns must stay in sync with the backend (mentions.ts MENTION_RE) and
// the composer (MentionInput) which inserts those tokens.
const TOKEN_RE = /@([a-f0-9]{24})|!\[([^\]]*)\]\(([a-f0-9]{24})\)|\[([^\]]+)\]\(([a-f0-9]{24})\)/gi;

export default function MentionText({ body, peopleIndex = {}, boardId }) {
  const [preview, setPreview] = useState(null);

  if (!body) return null;

  const out = [];
  let last = 0;
  const re = new RegExp(TOKEN_RE);
  let m;
  let key = 0;

  const openPreview = (e, attId, filename) => {
    e.preventDefault();
    e.stopPropagation();
    if (!boardId) return;
    setPreview({ url: attachmentDownloadUrl(boardId, attId), filename });
  };

  while ((m = re.exec(body)) !== null) {
    if (m.index > last) {
      out.push(renderMarkdownFragment(body.slice(last, m.index), `t${key++}`));
    }

    if (m[1]) {
      // @mention
      const uid = m[1];
      const p = peopleIndex[uid];
      const label = p
        ? `${p.firstname || ""} ${p.lastname || ""}`.trim() || p.email || uid
        : `@user`;
      out.push(
        <span key={`m${key++}`} className="mention-chip" title={uid}>
          @{label}
        </span>,
      );
    } else if (m[3]) {
      // image: ![alt](attId) — render thumbnail, click → lightbox
      const alt = m[2] || "image";
      const attId = m[3];
      const src = boardId ? attachmentDownloadUrl(boardId, attId) : "";
      out.push(
        <button
          key={`i${key++}`}
          type="button"
          className="update-image-link"
          onClick={(e) => openPreview(e, attId, alt)}
          title={`Preview ${alt}`}
        >
          <img src={src} alt={alt} className="update-image" loading="lazy" />
        </button>,
      );
    } else if (m[5]) {
      // generic file: [filename](attId) — click → lightbox (with download)
      const name = m[4];
      const attId = m[5];
      out.push(
        <button
          key={`f${key++}`}
          type="button"
          className="update-file-chip"
          onClick={(e) => openPreview(e, attId, name)}
        >
          <span className="d-inline-flex align-items-center gap-1"><Paperclip size={12} /> {name}</span>
        </button>,
      );
    }

    last = m.index + m[0].length;
  }
  if (last < body.length) {
    out.push(renderMarkdownFragment(body.slice(last), `t${key++}`));
  }

  return (
    <>
      {out}
      {preview && (
        <AttachmentPreview
          url={preview.url}
          filename={preview.filename}
          mime={preview.mime}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

// Renders the text between mention/attachment tokens with light Markdown.
// Block rules first (lists, then paragraphs), then inline rules per line.
function renderMarkdownFragment(text, keyPrefix) {
  if (!text) return null;
  const lines = text.split("\n");

  // Walk lines and group consecutive bullet/numbered lines into <ul>/<ol>.
  const blocks = [];
  let buf = [];
  let listKind = null;
  const flushList = () => {
    if (!buf.length) return;
    blocks.push({ kind: listKind, lines: buf });
    buf = [];
    listKind = null;
  };
  for (const ln of lines) {
    const ulMatch = /^[-*]\s+(.*)$/.exec(ln);
    const olMatch = /^\d+\.\s+(.*)$/.exec(ln);
    if (ulMatch) {
      if (listKind !== "ul") flushList();
      listKind = "ul";
      buf.push(ulMatch[1]);
    } else if (olMatch) {
      if (listKind !== "ol") flushList();
      listKind = "ol";
      buf.push(olMatch[1]);
    } else {
      flushList();
      blocks.push({ kind: "p", line: ln });
    }
  }
  flushList();

  const nodes = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const k = `${keyPrefix}_${i}`;
    if (b.kind === "ul" || b.kind === "ol") {
      const Tag = b.kind === "ul" ? "ul" : "ol";
      nodes.push(
        <Tag key={k} className={`update-md-list update-md-${b.kind}`}>
          {b.lines.map((l, j) => (
            <li key={`${k}_${j}`}>{renderInline(l, `${k}_${j}`)}</li>
          ))}
        </Tag>,
      );
    } else {
      // Plain line — preserve blank lines as spacers, terminate with <br>
      // unless it's the very last line in the fragment (the outer caller
      // will follow with another token or end of body).
      nodes.push(
        <React.Fragment key={k}>
          {renderInline(b.line, k)}
          {i < blocks.length - 1 && <br />}
        </React.Fragment>,
      );
    }
  }
  return <React.Fragment key={keyPrefix}>{nodes}</React.Fragment>;
}

// Inline parser: **bold**, *italic*, `code`, [text](http(s)://url).
// Operates by scanning the string with a combined regex, dispatching on
// which group matched. Anything that doesn't match passes through as-is.
const INLINE_RE = /\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*|`([^`\n]+?)`|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

function renderInline(text, keyPrefix) {
  if (!text) return null;
  const out = [];
  const re = new RegExp(INLINE_RE);
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<React.Fragment key={`${keyPrefix}_p${k++}`}>{text.slice(last, m.index)}</React.Fragment>);
    if (m[1] !== undefined) {
      out.push(<strong key={`${keyPrefix}_b${k++}`}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      out.push(<em key={`${keyPrefix}_i${k++}`}>{m[2]}</em>);
    } else if (m[3] !== undefined) {
      out.push(<code key={`${keyPrefix}_c${k++}`} className="update-md-code">{m[3]}</code>);
    } else if (m[4] !== undefined && m[5] !== undefined) {
      // URL is restricted to http(s) by the regex so an injected javascript:
      // payload would never reach this branch.
      out.push(
        <a key={`${keyPrefix}_l${k++}`} href={m[5]} target="_blank" rel="noopener noreferrer">
          {m[4]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<React.Fragment key={`${keyPrefix}_p${k++}`}>{text.slice(last)}</React.Fragment>);
  return out;
}
